import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useParams } from 'react-router-dom';
import styles from './AdditionalInformation.module.css';
import { axiosInstance } from '../../../../Authentication/AxiosInterceptor';
import { DDCell, DDRow } from '../../../../Types/AdditionalInformation/AdditionalInfoField';
import AdditionalInfoFieldRenderer from '../../../../Components/Utils/AdditionalInfoField/AdditionalInfoFieldRenderer';
import { flattenRows, updateFieldByType, createServerPayload } from '../../../../Utils/fieldTypeUtils';
import Tooltip from '../../../../Components/Utils/Tooltip/Tooltip';
import { useAppSelector } from '../../../../Redux/Store/Store';
import { selectRolePermissions } from '../../../../Redux/Store/Slices/questionnaire';
import { beginPendingSave } from '../../../../Hooks/useUnsavedChangesWarning';

const DEBOUNCE_MS = 500;

const AdditionalInformation = () => {
  const { areaId } = useParams();
  const [fields, setFields] = useState<DDCell[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const rolePermissions = useAppSelector(selectRolePermissions());
  const canEditGeneratedContent = rolePermissions?.editGeneratedContent ?? false;

  // ⬅️ refs for debouncing and fresh state access
  const timersRef = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());
  const endPendingByFieldRef = useRef<Map<string, () => void>>(new Map());
  const fieldsRef = useRef<DDCell[]>([]);
  const latestValuesRef = useRef<Map<string, any>>(new Map());

  // keep the ref in sync with state
  useEffect(() => {
    fieldsRef.current = fields;
  }, [fields]);

  // clear all timers on unmount
  useEffect(() => {
    return () => {
      timersRef.current.forEach((t) => clearTimeout(t));
      timersRef.current.clear();
      endPendingByFieldRef.current.forEach((end) => end());
      endPendingByFieldRef.current.clear();
    };
  }, []);

  const getDataBoxes = useCallback(async () => {
    if (!areaId) return;
    try {
      setLoading(true);
      const response = await axiosInstance.post<DDRow[]>(`GetDataBoxesByAreaId/${areaId}`);
      const flattenedCells = flattenRows(response.data);
      setFields(flattenedCells);
    } catch (error) {
      console.error('Error loading additional information:', error);
    } finally {
      setLoading(false);
    }
  }, [areaId]);

  useEffect(() => {
    getDataBoxes();
  }, [getDataBoxes]);

  // ⬅️ debounced server update function (per field)
  const scheduleServerUpdate = useCallback((fieldId: string) => {
    if (!canEditGeneratedContent) return;
    // clear any existing timer for this field
    const existing = timersRef.current.get(fieldId);
    if (existing) clearTimeout(existing);

    if (!endPendingByFieldRef.current.has(fieldId)) {
      endPendingByFieldRef.current.set(fieldId, beginPendingSave());
    }

    const timer = setTimeout(async () => {
      const value = latestValuesRef.current.get(fieldId);
      const currentField = fieldsRef.current.find(f => f.id === fieldId);
      if (!currentField || !areaId) {
        endPendingByFieldRef.current.get(fieldId)?.();
        endPendingByFieldRef.current.delete(fieldId);
        timersRef.current.delete(fieldId);
        return;
      }

      // ensure payload reflects the latest local state/value
      const fieldForPayload = updateFieldByType(currentField, value);
      const payload = createServerPayload(fieldForPayload, value);

      try {
        await axiosInstance.post(`UpdateAreaCell/${areaId}`, payload);
      } catch (error) {
        console.error('Error updating field:', error);
      } finally {
        // timer finished; remove reference
        timersRef.current.delete(fieldId);
        endPendingByFieldRef.current.get(fieldId)?.();
        endPendingByFieldRef.current.delete(fieldId);
      }
    }, DEBOUNCE_MS);

    timersRef.current.set(fieldId, timer);
  }, [areaId, canEditGeneratedContent]);

  const handleValueChange = useCallback(async (fieldId: string, newValue: any) => {
    if (!canEditGeneratedContent) {
      return;
    }
    // update local state immediately for snappy UI
    setFields(prevFields =>
      prevFields.map(field =>
        field.id === fieldId ? updateFieldByType(field, newValue) : field
      )
    );

    // record the latest value and schedule a debounced server write
    latestValuesRef.current.set(fieldId, newValue);
    scheduleServerUpdate(fieldId);
  }, [canEditGeneratedContent, scheduleServerUpdate]);

  // Memoize the field list to avoid unnecessary re-renders
  const fieldList = useMemo(() => fields, [fields]);

  const loadingComponent = useMemo(() => (
    <div className={styles.container}>
      <div className={styles.emptyState}>
        <p>Loading additional information...</p>
      </div>
    </div>
  ), []);

  const emptyStateComponent = useMemo(() => (
    <div className={styles.container}>
      <div className={styles.emptyState}>
        <p>No additional information fields have been defined for this template.</p>
      </div>
    </div>
  ), []);

  if (loading) return loadingComponent;
  if (fields.length === 0) return emptyStateComponent;

  return (
    <div className={styles.container}>
      <div className={styles.fieldsGrid}>
        {fieldList.map(field => (
          <Tooltip
            key={field.id}
            text={`Your role as a ${rolePermissions?.roleTitle} means that you cannot edit the additional information`}
            align="bottom"
            disabled={canEditGeneratedContent}
          >
            <div className={styles.fieldWrapper}>
              <AdditionalInfoFieldRenderer
                field={field}
                onValueChange={handleValueChange}
                readonly={!canEditGeneratedContent}
              />
            </div>
          </Tooltip>
        ))}
      </div>
    </div>
  );
};

export default AdditionalInformation;
